import { segmentTrackBusSelectionFromMap } from "analytics/analyticTrackEvents";
import { useFavoriteBuses } from "contexts/FavoriteBusesContext/FavoriteBusesContext";
import { useFavoriteScopedBusesContext } from "contexts/FavoriteBusesContext/FavoriteScopedBusesContext";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import useSelectedBuses from "contexts/SelectedBusesContext/useSelectedBuses";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import React from "react";
import {
    getCapacityColor,
    getConstraintCountColor
} from "sharedStyles/capacityColorStyles";
import {
    ColorMode,
    DEFAULT_CONSTRAINT_COUNT_THRESHOLD
} from "types/busFilterTypes";
import { BusId, ScopedBus } from "types/busType";
import { areColocated } from "types/coordinatesAndZoom";
import FavoriteBusMarker from "./FavoriteBusMarker";

type FavoriteBusOverlayProps = {
    // The buses the circle layer is drawing, so a star lines up with a bus that
    // is actually on the map.
    readonly busesToDisplay: ReadonlyArray<ScopedBus>;
    readonly possibleSelectedBusIds: ReadonlyArray<BusId | undefined>;
};

/** Star markers for the current region's favorites, drawn over the circle layer. */
const FavoriteBusOverlay: React.FC<FavoriteBusOverlayProps> = ({
    busesToDisplay,
    possibleSelectedBusIds
}) => {
    const { generator: generatorType } = useMapViewRoutingMetadata();
    const { selectedBusesAtLocation, selectMultipleBuses } = useSelectedBuses();
    const { favorites } = useFavoriteBuses();
    const favoriteScopedBuses = useFavoriteScopedBusesContext();
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const { colorMode, capacityThresholds, constraintCountThresholds } =
        busFilters;
    const { isColorblindMode } = useUserDataContext();

    const isConstraintCountMode = colorMode === ColorMode.CONSTRAINT_COUNT;
    const colorThresholds = isConstraintCountMode
        ? constraintCountThresholds ?? DEFAULT_CONSTRAINT_COUNT_THRESHOLD
        : capacityThresholds;

    // Only favorites in the active dataset get a star.
    const favoriteBusesInView = busesToDisplay.filter((scopedBus) =>
        favorites.includes(scopedBus.bus.niraBusId)
    );

    // A favorite selected from the panel may be filtered out of the map data.
    // Star that one bus so the selection is visible, without unfiltering the
    // rest of the favorites.
    const maybeSelectedFavoriteOutOfView = favoriteScopedBuses.find(
        (scopedBus) =>
            scopedBus.bus.id === selectedBusesAtLocation?.selectedBusId &&
            !busesToDisplay.some(
                (candidate) => candidate.bus.id === scopedBus.bus.id
            )
    );

    const favoriteBusesToDisplay =
        maybeSelectedFavoriteOutOfView === undefined
            ? favoriteBusesInView
            : [...favoriteBusesInView, maybeSelectedFavoriteOutOfView];

    // Stars sit above the circle layer, so they select the same way it does:
    // every bus within the colocated radius, not just the favorite.
    const selectFavoriteBus = (scopedBus: ScopedBus) => {
        const busesAtLocation = busesToDisplay.filter((candidate) =>
            areColocated(candidate.bus, scopedBus.bus)
        );
        const busIds = busesAtLocation.map((candidate) => candidate.bus.id);
        selectMultipleBuses({
            selectedBusId: scopedBus.bus.id,
            // A favorite outside the map data is in no location group of its
            // own, so page it alone rather than into an empty list.
            busIds: busIds.includes(scopedBus.bus.id)
                ? busIds
                : [scopedBus.bus.id]
        });
        segmentTrackBusSelectionFromMap(scopedBus);
    };

    return (
        <>
            {favoriteBusesToDisplay.map((scopedBus) => (
                <FavoriteBusMarker
                    key={scopedBus.bus.id}
                    scopedBus={scopedBus}
                    color={
                        isConstraintCountMode
                            ? getConstraintCountColor(
                                  scopedBus.zeroMwConstraintCount ?? 0,
                                  colorThresholds,
                                  isColorblindMode
                              )
                            : getCapacityColor(
                                  scopedBus.scopedCapacityEnergyCost,
                                  capacityThresholds,
                                  generatorType,
                                  isColorblindMode
                              )
                    }
                    isSelected={possibleSelectedBusIds.includes(
                        scopedBus.bus.id
                    )}
                    onSelect={() => selectFavoriteBus(scopedBus)}
                />
            ))}
        </>
    );
};

export default FavoriteBusOverlay;
